import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { isEqual, sortBy } from "lodash"
import { prop } from "ramda"
import { Symbol } from "../../../../constants"
import type { Pricing, PricingResponse } from "../../../../graphql/types"
import { getSelectedLocationOverrideId } from "../../../common/locationsPicker/selectors"
import type { StateObject } from "../../../redux/types"
import { getRecordEditId } from "../../../router/selectors"
import { computeOverriddenValue } from "../../categories/utils"
import type { PricingFormItem } from "./PricingForm"
import type { PricingTableDataItem } from "./PricingTable"
import type { PricingsState } from "./reducer"

export const getPricingStateObject = (state: StateObject): PricingsState => state.pricing

export const getPricingsData = (state: StateObject): Maybe<PricingResponse> => getPricingStateObject(state).data

export const getPricingById = (state: StateObject, pricingId: Maybe<string>): Maybe<Pricing> => {
    const pricingData = getPricingsData(state)

    if (pricingData && pricingData.data) {
        return pricingData.data.find(c => c.id === pricingId)
    }

    return null
}

export const getEditPricingId = (state: StateObject) => getRecordEditId(state)

export const getEditPricing = (state: StateObject): Maybe<Pricing> => {
    const pricingData = getPricingsData(state)

    if (pricingData && pricingData.data) {
        return pricingData.data.find(c => c.id === getEditPricingId(state))
    }

    return null
}

export const handlePricingValue = (pricing: Pricing) => {
    if (pricing.absolute && !pricing.percentage) {
        return pricing.absolute > 0
            ? `${Symbol.Plus}$${Math.abs(pricing.absolute) / 100}`
            : `${Symbol.Minus}$${Math.abs(pricing.absolute) / 100}`
    } else if (pricing.percentage && !pricing.absolute) {
        return pricing.percentage > 0
            ? `${Symbol.Plus}${Math.abs(pricing.percentage) / 1000}%`
            : `${Symbol.Minus}${Math.abs(pricing.percentage) / 1000}%`
    } else {
        return "0"
    }
}

export const getDataForPricingTable = (state: StateObject): PricingTableDataItem[] => {
    const selectedLocationId = getSelectedLocationOverrideId(state)
    const pricingsData = getPricingsData(state)

    if (!pricingsData) {
        return []
    }

    const result = pricingsData.data.map((pricing: Pricing) => {
        if (!selectedLocationId) {
            return {
                pricingId: pricing.id,
                name: pricing.name,
                amount: handlePricingValue(pricing),
                isApplicable: pricing.isActive,
                isAutoApply: pricing.isAutoApply,
                isOrderLevel: pricing.isOrderLevel,
                isItemLevel: pricing.isItemLevel,
                sortIndex: pricing.sortIndex,
            }
        } else {
            const isActiveOverriddenValue = computeOverriddenValue(
                pricingsData.overrides,
                selectedLocationId,
                pricing.id,
                prop("isActive")
            )
            const isAutoApplyOverridenValue = computeOverriddenValue(
                pricingsData.overrides,
                selectedLocationId,
                pricing.id,
                prop("isAutoApply")
            )
            return {
                pricingId: pricing.id,
                name: pricing.name,
                amount: handlePricingValue(pricing),
                isOrderLevel: pricing.isOrderLevel,
                isItemLevel: pricing.isItemLevel,
                isApplicable: isActiveOverriddenValue != null ? isActiveOverriddenValue : pricing.isActive,
                isApplicableOverriden: isActiveOverriddenValue != null && isActiveOverriddenValue !== pricing.isActive,
                isAutoApply: isAutoApplyOverridenValue != null ? isAutoApplyOverridenValue : pricing.isAutoApply,
                isAutoApplyOverriden:
                    isAutoApplyOverridenValue != null && isAutoApplyOverridenValue !== pricing.isAutoApply,
                sortIndex: pricing.sortIndex,
            }
        }
    })

    return sortBy(result, "sortIndex")
}

export const isPricingFormLoading = (state: StateObject): boolean => {
    const isEditing = getRecordEditId(state) != null
    const formData = getPricingFormData(state)
    return isEditing && formData == null
}

export const getPricingFormData = (state: StateObject): Maybe<PricingFormItem> => {
    const pricing = getEditPricing(state)
    const locationId = getSelectedLocationOverrideId(state)
    const pricingsData = getPricingsData(state)

    if (!pricing || !pricingsData) {
        return null
    }

    const value = pricing.absolute != null ? (pricing.absolute ?? 0) : (pricing.percentage ?? 0)
    const sign = value < 0 ? `${Symbol.Minus}` : `${Symbol.Plus}`

    if (!locationId) {
        return {
            pricingId: pricing.id,
            name: pricing.name,
            isApplicable: pricing.isActive,
            autoApply: pricing.isAutoApply,
            value: Math.abs(value),
            type: pricing.absolute != null ? "absolute" : "percentage",
            sign: sign,
            isOrderLevel: pricing.isOrderLevel,
            isItemLevel: pricing.isItemLevel,
            sortIndex: pricing.sortIndex,
            autoApplyHours: pricing.autoApplyHours,
        }
    } else {
        const isActiveOverriddenValue = computeOverriddenValue(
            pricingsData.overrides,
            locationId,
            pricing.id,
            prop("isActive")
        )
        const autoApplyOverriddenValue = computeOverriddenValue(
            pricingsData.overrides,
            locationId,
            pricing.id,
            prop("isAutoApply")
        )
        const autoApplyHoursOverridenValue = computeOverriddenValue(
            pricingsData.overrides,
            locationId,
            pricing.id,
            prop("autoApplyHours")
        )
        return {
            id: pricing.id,
            pricingId: pricing.id,
            name: pricing.name,
            // @ts-expect-error
            isApplicable: isActiveOverriddenValue != null ? isActiveOverriddenValue : pricing.isActive,
            isApplicableOverriden: isActiveOverriddenValue != null && isActiveOverriddenValue !== pricing.isActive,
            // @ts-expect-error
            autoApply: autoApplyOverriddenValue != null ? autoApplyOverriddenValue : pricing.isAutoApply,
            autoApplyOverriden: autoApplyOverriddenValue != null && autoApplyOverriddenValue !== pricing.isAutoApply,
            value: Math.abs(value),
            type: pricing.absolute ? "absolute" : "percentage",
            sign: sign,
            isOrderLevel: pricing.isOrderLevel,
            isItemLevel: pricing.isItemLevel,
            sortIndex: pricing.sortIndex,
            // autoApplyHours: pricing.autoApplyHours,
            // @ts-expect-error
            autoApplyHours:
                autoApplyHoursOverridenValue != null ? autoApplyHoursOverridenValue : pricing.autoApplyHours,
            autoApplyHoursOverriden:
                autoApplyHoursOverridenValue != null &&
                !isEqual(
                    // @ts-expect-error
                    autoApplyHoursOverridenValue.timeSpans,
                    pricing.autoApplyHours ? pricing.autoApplyHours.timeSpans : []
                ),
        }
    }
}
